import { useHead } from "@unhead/react"
import Header from "@/components/Header"
import EMICalculator from "@/components/EMICalculator"
import LoanSections from "@/components/LoanSections"
import NicheCalculators from "@/components/NicheCalculators"
import EligibilitySection from "@/components/EligibilitySection"
import Footer from "@/components/Footer"

export default function Home() {
  useHead({
    title: "EMI Online Calculator - India's Most Accurate EMI Calculator for Home, Car & Personal Loans",
    meta: [
      {
        name: "description",
        content:
          "Calculate EMI instantly for HDFC, SBI, Axis Bank loans. Free online EMI calculator with amortization schedule, GST inclusion, and partial payment options for home loans, car loans, and personal loans in India.",
      },
      {
        name: "keywords",
        content:
          "EMI calculator, HDFC home loan premium calculator, SBI online housing loan EMI calculator, India car loan calculator, personal loan calculator INR, 5 lakh personal loan calculator, gold loan calculator online, two wheeler emi calculator",
      },
    ],
    htmlAttrs: { lang: "en" },
  })

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      {/* Hero Banner */}
      <div className="bg-gradient-to-br from-blue-800 via-blue-700 to-blue-900 py-10 md:py-14">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-blue-200 text-sm font-medium mb-2 tracking-wide uppercase">
            Trusted by 10 Lakh+ Indians
          </p>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
            EMI Online Calculator
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-6">
            Compare bank rates instantly. Get a full amortization schedule. Calculate eligibility for SBI, HDFC, and Axis Bank.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="#calculator"
              className="bg-white text-blue-800 font-semibold px-6 py-3 rounded-xl hover:bg-blue-50 transition-colors shadow-lg"
            >
              Calculate EMI Now
            </a>
            <a
              href="#eligibility"
              className="bg-blue-600/50 backdrop-blur text-white font-medium px-6 py-3 rounded-xl border border-blue-400/30 hover:bg-blue-600/70 transition-colors"
            >
              Check Eligibility
            </a>
          </div>
        </div>
      </div>

      {/* Main Calculator */}
      <div className="bg-slate-50">
        <EMICalculator />
      </div>

      {/* Loan Sections (Home, Car, Personal) */}
      <LoanSections />

      {/* Niche Calculators */}
      <NicheCalculators />

      {/* Eligibility Section */}
      <EligibilitySection />

      {/* SEO Content Block */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">
            How to Calculate EMI for Any Loan in India
          </h2>
          <div className="prose prose-slate max-w-none text-sm leading-relaxed text-slate-600 space-y-4">
            <p>
              Our tool helps you calculate EMIs for 5 lakh personal loans, used car loans from HDFC, and even simple interest gold loans, providing a complete breakdown of interest rates and tenure. Whether you need an HDFC home loan premium calculator, an SBI online housing loan EMI calculator, or an India car loan calculator, our platform delivers instant, accurate results.
            </p>
            <p>
              The EMI (Equated Monthly Installment) is calculated using the standard reducing balance formula: EMI = P x R x (1+R)^N / ((1+R)^N - 1), where P is the principal loan amount, R is the monthly interest rate, and N is the number of monthly installments. This formula is used by all major Indian banks including HDFC, SBI, ICICI, and Axis Bank.
            </p>
            <p>
              For personal loans in INR, the typical interest rates range from 10.50% to 16% per annum depending on your credit score and income. Our 5 lakh personal loan calculator shows you exactly how much you will pay monthly, including the option to factor in GST on processing fees. This is especially useful for those searching for a personal loan calculator with GST to get the true cost of borrowing.
            </p>
            <p>
              If you are looking for a housing loan partial payment calculator, our tool shows you exactly how much interest you can save by making a lump-sum prepayment during your loan tenure. Partial payments directly reduce your outstanding principal, which can significantly shorten your loan tenure or reduce your monthly EMI burden.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
